import { convertTimeZone } from '@magicdoorinc/date';
import { leaseRepository } from '~/repositories/leaseRepository';
import { propertiesRepository } from '~/repositories/propertiesRepository';
import { PropertyManagersRepository } from '~/repositories/propertyManagersRepository';
import { tenantRepository } from '~/repositories/tenantRepository';
import { unitRepository } from '~/repositories/unitRepository';
import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { maintenanceServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  ConvertMaintenanceRequestToWorkOrderRequestDto,
  CreateMaintenanceRequestRequestDto,
  HistoryPageDto,
  MaintenanceRequestCategoryDto,
  MaintenanceRequestDetailDto,
  MaintenanceRequestListItemDto,
  MaintenanceRequestsStats,
  UpdateMaintenanceRequestRequestDto,
  HydratedPropertyDto,
  HydratedUnitDto,
  HydratedTenantDto,
} from '~/swagger/maintenance/Api';
import type { HydratedWorkOrderDto, MaintenanceRequestStatus, Urgency } from '~/swagger/portal/Api';

const propertyManagersRepository = new PropertyManagersRepository();

const dedupeById = <T extends { id: string }>(rows: T[]): T[] => [...new Map(rows.map((row) => [row.id, row])).values()];

export const MAINTENANCE_REQUEST_URL = `${maintenanceServiceUrl()}/company-portal/maintenance-requests`;
export type MaintenanceRequestFilter = {
  page?: number;
  pageSize?: number;
  portfolioId?: string;
  propertyId?: string;
  unitId?: string;
  leaseId?: string;
  tenantId?: string;
  runBookId?: string;
  status?: `${MaintenanceRequestStatus}` | `${MaintenanceRequestStatus}`[];
  urgency?: `${Urgency}`;
  searchText?: string;
  created?: { start?: string; end?: string };
  new?: boolean;
};

// The Maintenance service now returns IDs-only pages; fetch the referenced
// entities from their owning services here so the UI keeps its previous shape.
type MaintenanceRequestsPage = {
  items: MaintenanceRequestListItemDto[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

export type HydratedMaintenanceRequestsList = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  items: MaintenanceRequestListItemDto[];
  properties: HydratedPropertyDto[];
  units: HydratedUnitDto[];
  tenants: HydratedTenantDto[];
};

const uniqueMrIds = (ids: (string | null | undefined)[]): string[] => [...new Set(ids.filter((id): id is string => !!id))];

export class MaintenanceRequestRepository extends BaseRestRepository {
  public async getMaintenanceRequests(filter?: MaintenanceRequestFilter): Promise<HydratedMaintenanceRequestsList> {
    const url = urlWithQuery(MAINTENANCE_REQUEST_URL, filter);
    const response = await this.fetchWithAuth(url);
    const page = (await this.getJsonResponse(response)) as MaintenanceRequestsPage;
    const items = page.items ?? [];
    const tenantIds = uniqueMrIds(items.map((item) => item.tenantId));
    const tenants = tenantIds.length ? await tenantRepository.getTenantsByIds({ tenantIds }) : [];

    // Property/unit name + address now arrive embedded on each item's `location`, so the
    // property/unit lookups the UI joins against are built from that instead of re-fetching.
    const properties = dedupeById(
      items
        .filter((item) => item.propertyId)
        .map((item) => ({
          id: item.propertyId,
          name: item.location?.propertyName ?? undefined,
          address: item.location?.address ?? undefined,
        }))
    ) as unknown as HydratedPropertyDto[];
    const units = dedupeById(
      items.filter((item) => item.unitId).map((item) => ({ id: item.unitId, name: item.location?.unitName ?? undefined }))
    ) as unknown as HydratedUnitDto[];

    return {
      currentPage: page.page,
      totalPages: page.totalPages,
      pageSize: page.pageSize,
      totalCount: page.totalCount,
      items,
      properties,
      units,
      tenants: tenants as unknown as HydratedTenantDto[],
    };
  }
  // The Maintenance service returns IDs-only detail responses; fetch the referenced
  // entities from their owning services so the detail screen keeps its shape.
  private async hydrateMaintenanceRequestDetail(raw: any): Promise<MaintenanceRequestDetailDto> {
    const [property, unit, lease, tenants, propertyManager, categories, workOrder] = await Promise.all([
      raw.propertyId ? propertiesRepository.getProperty(raw.propertyId).catch(() => undefined) : undefined,
      raw.unitId ? unitRepository.batchGetUnitsByIds([raw.unitId]).then((units) => units[0]) : undefined,
      raw.leaseId ? leaseRepository.batchGetLeasesByIds([raw.leaseId]).then((leases) => leases[0]) : undefined,
      raw.tenantId ? tenantRepository.getTenantsByIds({ tenantIds: [raw.tenantId] }) : undefined,
      raw.assignedPropertyManagerId
        ? propertyManagersRepository.getPropertyManagerById(raw.assignedPropertyManagerId).catch(() => undefined)
        : undefined,
      raw.categoryId ? this.getMaintenanceRequestCategory() : undefined,
      raw.workOrderId ? this.getLinkedWorkOrder(raw.workOrderId) : undefined,
    ]);

    return {
      ...raw,
      property,
      // property.owners is the nested { owner, ownershipPercentage } shape; the detail expects flat owners.
      owners: (property?.owners ?? []).map((propertyOwner: any) => ({
        ...propertyOwner.owner,
        ownershipPercentage: propertyOwner.ownershipPercentage,
      })),
      unit,
      lease,
      tenant: tenants?.[0],
      assignedPropertyManagerId: raw.assignedPropertyManagerId,
      propertyManager,
      category: raw.categoryId ? categories?.find((category) => category.id === raw.categoryId) : undefined,
      workOrder,
    } as MaintenanceRequestDetailDto;
  }

  private async getLinkedWorkOrder(workOrderId: string): Promise<any> {
    const url = `${maintenanceServiceUrl()}/company-portal/work-orders/${workOrderId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response).catch(() => undefined);
  }

  public async getMaintenanceRequest(maintenanceRequestId: string): Promise<MaintenanceRequestDetailDto> {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}`;
    const response = await this.fetchWithAuth(url);

    return this.hydrateMaintenanceRequestDetail(await this.getJsonResponse(response));
  }

  // Raw, un-hydrated fetch for the linked-MR summary/chat, which only reads scalar
  // fields (title/status/created/id/chatId/categoryId).
  public async getMaintenanceRequestSummary(maintenanceRequestId: string): Promise<MaintenanceRequestDetailDto> {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getMaintenanceRequestHistory(
    maintenanceRequestId: string,
    filter?: { page?: number; pageSize?: number }
  ): Promise<HistoryPageDto> {
    validateId(maintenanceRequestId);
    const url = urlWithQuery(`${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/history`, filter ?? {});
    const response = await this.fetchWithAuth(url);
    const page = (await this.getJsonResponse(response)) as Pick<HistoryPageDto, 'items'> & {
      page: number;
      totalPages: number;
      pageSize: number;
      totalCount: number;
    };
    return { currentPage: page.page, totalPages: page.totalPages, pageSize: page.pageSize, totalCount: page.totalCount, items: page.items };
  }

  public async addMaintenanceRequest(maintenanceRequest: CreateMaintenanceRequestRequestDto): Promise<MaintenanceRequestDetailDto> {
    maintenanceRequest.due = convertTimeZone(maintenanceRequest.due).format('YYYY-MM-DD');
    const response = await this.fetchWithAuth(MAINTENANCE_REQUEST_URL, {
      method: 'POST',
      body: JSON.stringify(maintenanceRequest),
    });
    return this.hydrateMaintenanceRequestDetail(await this.getJsonResponse(response));
  }

  public async deleteMaintenanceRequest(maintenanceRequestId: string): Promise<void> {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async disableMaintenanceRequestAi(maintenanceRequestId: string): Promise<void> {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/ai`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async updateMaintenanceRequest(
    maintenanceRequestId: string,
    maintenanceRequest: UpdateMaintenanceRequestRequestDto
  ): Promise<MaintenanceRequestDetailDto> {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(maintenanceRequest),
    });
    return this.hydrateMaintenanceRequestDetail(await this.getJsonResponse(response));
  }

  public async updateMaintenanceRequestStatus(maintenanceRequestId: string, status: MaintenanceRequestStatus) {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/status`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  public async updateMaintenanceRequestUrgency(maintenanceRequestId: string, urgency: Urgency) {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/urgency`;

    await this.fetchWithAuth(url, { method: 'PUT', body: JSON.stringify({ urgency }) });
  }

  public async getMaintenanceRequestStats(): Promise<MaintenanceRequestsStats> {
    const url = `${MAINTENANCE_REQUEST_URL}/stats`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getMaintenanceRequestCategory(): Promise<MaintenanceRequestCategoryDto[]> {
    const url = `${MAINTENANCE_REQUEST_URL}/categories`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async uploadMaintenanceRequestFiles(maintenanceRequestId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(maintenanceRequestId, 'MaintenanceRequest');
    if (files.length === 0) return [];

    const uploadSessionIds = await uploadSessionRepository.uploadFiles(
      `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/files/upload-sessions`,
      files,
      opts
    );

    await this.fetchWithAuth(`${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/files`, {
      method: 'POST',
      body: JSON.stringify({ files: uploadSessionIds.map((uploadSessionId) => ({ uploadSessionId })) }),
    });

    return uploadSessionIds;
  }

  public async deleteMaintenanceRequestFile(maintenanceRequestId: string, fileId: string): Promise<void> {
    validateId(maintenanceRequestId, 'MaintenanceRequest');
    validateId(fileId, 'File');
    await this.fetchWithAuth(`${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/files/${fileId}`, { method: 'DELETE' });
  }

  /** @deprecated Use deleteMaintenanceRequestFile instead. */
  public async deleteFile(maintenanceRequestId: string, fileId: string): Promise<void> {
    await this.deleteMaintenanceRequestFile(maintenanceRequestId, fileId);
  }

  public async convertToWorkOrder(
    maintenanceRequestId: string,
    payload: ConvertMaintenanceRequestToWorkOrderRequestDto
  ): Promise<HydratedWorkOrderDto> {
    validateId(maintenanceRequestId);
    const url = `${MAINTENANCE_REQUEST_URL}/${maintenanceRequestId}/convert-to-work-order`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}
